import {
  faCheck,
  faFloppyDisk,
  faFloppyDiskCircleXmark,
  faGears,
  faSparkles,
  faTrash
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import clsx from 'clsx'
import DOMPurify from 'dompurify'
import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import {
  type GenerateReportResponse,
  type InterviewReportPayload,
  useApplicantReportGeneration,
  useInterviewReportDeleteMutation,
  useInterviewReportMutation
} from '~/api/mutations/applicant'
import type { ApplicantReportsResponse } from '~/api/queries/applicant'
import { profilesListStandardHeight } from '~/components/Global/Elements/ProfilesList/ProfilesList.css'
import {
  Button,
  ButtonVariation
} from '~/components/Global/Interactions/Button'
import { ToastType } from '~/components/Global/Interactions/Toast/ToastType'
import { useConfig } from '~/domains/configuration/queries/config'
import { UsersList } from '~/domains/users/components/UsersList'
import { formatRelativeTime, toast } from '~/utils'
import { confirm } from '~/utils/confirm'
import { TypeWriter } from '~/utils/typewriter'
import {
  reportItemBlur,
  reportItemGeneratingPlaceholder,
  reportItemLabel,
  reportItemSections,
  reportItemTitle,
  reportItemValue,
  reportsListingButton,
  reportsListingButtonColors,
  reportsListingHeaderFont,
  reportsListingItem,
  reportsListingItemBack,
  reportsListingItemBackground,
  reportsListingItemDelete,
  reportsListingItemHeader,
  reportsListingItemHeaderActions,
  reportsListingItemNew,
  reportsListingItemNotEditing,
  reportsListingItemSubTitle,
  reportsListingItemTitle,
  reportsListingItemTitleWrapper
} from './ApplicantReports.css'

export const ApplicantReportItem = ({
  applicantId,
  data,
  showEdit = false,
  launchGeneration = false,
  onChangeCurrentEdit
}: {
  applicantId: number
  data?: ApplicantReportsResponse
  showEdit?: boolean
  launchGeneration?: boolean
  onChangeCurrentEdit: (value: 'new' | number | null) => void
}) => {
  const { data: defaultSectionsConfig } = useConfig('interview_report.sections')
  const updateMutation = useInterviewReportMutation(applicantId)
  const deleteMutation = useInterviewReportDeleteMutation(applicantId)
  const generateReportMutation = useApplicantReportGeneration(applicantId)
  const [currentId, setCurrentId] = useState<number | undefined>(data?.id)
  const [formData, setFormData] = useState({
    title: data?.title || '',
    sections: data?.interviewReportSections || []
  })
  const [showUpdateStatus, setShowUpdateStatus] = useState(false)
  const [isContentEditable, setIsContentEditable] = useState(false)
  const [lastGeneratedReport, setLastGeneratedReport] =
    useState<GenerateReportResponse | null>(null)
  const itemRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (
      lastGeneratedReport?.success !== true ||
      generateReportMutation.isPending === true ||
      updateMutation.isPending === true ||
      typeof currentId === 'number'
    ) {
      return
    }
    formData.sections.forEach((section, index) => {
      const contentToWrite = lastGeneratedReport.content.filter(
        (_, key) => key === index
      )[0]?.content
      const sectionSpan = document.querySelector(
        `span#report_new_section_${index}`
      )
      if (contentToWrite && sectionSpan) {
        TypeWriter(contentToWrite, 50, sectionSpan, ' ')
        section.content = contentToWrite
      }
    })
  }, [
    lastGeneratedReport,
    formData.sections,
    currentId,
    generateReportMutation.isPending,
    updateMutation.isPending
  ])

  useEffect(() => {
    // Init form data with default sections if no data
    if (!data?.id && defaultSectionsConfig && !formData.sections.length) {
      setFormData({
        title: '',
        sections: defaultSectionsConfig.map(({ value }) => ({
          name: value,
          content: ''
        }))
      })
    }
  }, [data, defaultSectionsConfig, formData.sections.length])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (itemRef.current && !itemRef.current.contains(e.target as Node)) {
        setIsContentEditable(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (updateMutation.isPending || updateMutation.isError) {
      setShowUpdateStatus(true)
    }
  }, [updateMutation.isPending, updateMutation.isError])

  if (!showEdit && data?.id) {
    return (
      <div
        className={clsx(
          reportsListingItem,
          data?.id ? reportsListingItemNotEditing : reportsListingItemNew
        )}
        onClick={() => onChangeCurrentEdit(data?.id ? data.id : 'new')}
      >
        {data?.createdBy && (
          <UsersList
            className={profilesListStandardHeight}
            users={
              data?.modifiedBy && data.modifiedBy.id !== data.createdBy.id
                ? [data?.createdBy, data?.modifiedBy]
                : [data?.createdBy]
            }
          />
        )}
        <div className={reportsListingItemTitleWrapper}>
          {data && (
            <div className={reportsListingItemTitle}>
              {data?.title
                ? data?.title
                : `Compte rendu du ${new Date(data.created).toLocaleDateString()}`}
            </div>
          )}

          {data?.id && (
            <div className={reportsListingItemSubTitle}>
              {data ? formatRelativeTime(new Date(data.created)) : ''}
            </div>
          )}
        </div>
        {data?.id && (
          <button
            type="button"
            className={reportsListingItemDelete}
            onClick={async (e) => {
              e.stopPropagation()
              confirm(
                'Supprimer ce compte rendu ?',
                'Ce compte rendu sera définitivement supprimé.',
                'Supprimer'
              ).then((confirmed) => {
                if (!confirmed) return
                deleteMutation.mutate({ id: data.id })
              })
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        )}
      </div>
    )
  }

  const generateReport = () => {
    if (generateReportMutation.isPending === true) {
      return
    }

    generateReportMutation.mutate(null, {
      onSuccess: (data) => {
        if (data?.success) {
          setLastGeneratedReport(data)
        } else {
          setLastGeneratedReport(null)
          toast(
            'Compte-rendu candidat',
            'Erreur de génération',
            ToastType.Danger
          )
        }
      },
      onError: (error) => {
        setLastGeneratedReport(null)
        let msg = 'Erreur de génération'
        if (error?.httpStatus === 404) {
          msg =
            "Génération impossible : le CV ou son contenu n'est pas disponible"
        }
        toast('Compte-rendu candidat', msg, ToastType.Danger)
      }
    })
  }

  const updateReport = (
    newFormData: InterviewReportPayload,
    force?: boolean
  ) => {
    // Do not update/create if form is empty
    if (
      newFormData.title === '' &&
      newFormData.sections.every((s) => s.content === '')
    ) {
      return
    }
    // Do not update if form state is the same
    if (
      force !== true &&
      newFormData.title === formData.title &&
      formData.sections.every(
        (s) =>
          newFormData.sections.find((ns) => ns.name === s.name)?.content ===
          s.content
      )
    ) {
      return
    }
    setFormData(newFormData)
    if (generateReportMutation.isPending === true) {
      return
    }

    // Do not update if mutation is pending and no currentId (avoid creating multiple reports)
    // Keep form data in state to update it further @todo ensure this is actually done further
    if (updateMutation.isPending && !currentId) {
      return
    }
    updateMutation.mutate(
      {
        id: currentId || undefined,
        ...newFormData
      },
      {
        onSuccess: ({ id }) => {
          setCurrentId(id)
          onChangeCurrentEdit(id)
        }
      }
    )
  }

  if (
    lastGeneratedReport === null &&
    !generateReportMutation.isPending &&
    !generateReportMutation.isError &&
    launchGeneration === true
  ) {
    generateReport()
  }

  return (
    <motion.div
      variants={{
        initial: { opacity: 0, x: 40 },
        hidden: { opacity: 0, x: 40 },
        visible: { opacity: 1, x: 0 }
      }}
      className={clsx(
        reportsListingItem,
        typeof currentId !== 'number'
          ? reportsListingItemBackground.draft
          : null
      )}
      ref={itemRef}
    >
      <div className={reportsListingItemHeader}>
        <button
          type="button"
          className={reportsListingItemBack}
          onClick={() => onChangeCurrentEdit(null)}
        >
          &lt; Retour
        </button>
        {!showUpdateStatus && typeof currentId !== 'number' && (
          <div className={reportsListingItemHeaderActions}>
            <Button
              buttonSize="extraSmall"
              variation={ButtonVariation.AI}
              disabled={generateReportMutation.isPending}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                generateReport()
              }}
            >
              <FontAwesomeIcon icon={faSparkles} />{' '}
              {lastGeneratedReport === null ? 'Générer' : 'Re-générer'}
            </Button>
            {!generateReportMutation.isPending && lastGeneratedReport && (
              <span
                className={reportsListingButton}
                onClick={() => {
                  const newSections = formData.sections.map((s, index) => {
                    const sectionSpan = document.getElementById(
                      `report_${data?.id ?? 'new'}_section_${index}`
                    )
                    return {
                      ...s,
                      content: sectionSpan?.innerHTML || ''
                    }
                  })
                  const newTitle = document.getElementById(
                    `report_${data?.id ?? 'new'}_title`
                  )
                  updateReport(
                    {
                      ...formData,
                      sections: newSections,
                      title:
                        newTitle?.textContent !== formData.title
                          ? (newTitle?.textContent ?? '')
                          : formData.title
                    },
                    true
                  )
                }}
              >
                <FontAwesomeIcon icon={faFloppyDisk} /> Sauvegarder
              </span>
            )}
          </div>
        )}
        {!generateReportMutation.isPending && showUpdateStatus && (
          <div
            className={clsx(
              reportsListingItemSubTitle,
              reportsListingItemHeaderActions
            )}
          >
            {updateMutation.isError && (
              <div>
                <FontAwesomeIcon icon={faFloppyDiskCircleXmark} /> Erreur lors
                de la sauvegarde du compte rendu
              </div>
            )}
            {updateMutation.isPending && (
              <div>
                <FontAwesomeIcon icon={faFloppyDisk} /> Sauvegarde en cours...
              </div>
            )}
            {updateMutation.isSuccess && (
              <div>
                <FontAwesomeIcon icon={faCheck} /> Compte rendu sauvegardé
              </div>
            )}
          </div>
        )}
      </div>
      <div
        id={`report_${data?.id ?? 'new'}_title`}
        contentEditable={isContentEditable}
        onClick={() => setIsContentEditable(true)}
        className={reportItemTitle}
        onBlur={(e) =>
          updateReport({
            ...formData,
            title:
              e.currentTarget.textContent ||
              `Compte rendu du ${new Date().toLocaleDateString()}`
          })
        }
        data-placeholder={'Saisir un titre'}
        suppressContentEditableWarning={true}
      >
        {formData?.title
          ? formData?.title
          : `Compte rendu du ${new Date().toLocaleDateString()}`}
      </div>
      <div className={reportItemSections} style={{ position: 'relative' }}>
        {generateReportMutation.isPending && (
          <div className={clsx(reportItemGeneratingPlaceholder)}>
            <div
              className={clsx(
                reportsListingButton,
                reportsListingHeaderFont,
                reportsListingButtonColors.aiLoading
              )}
            >
              <FontAwesomeIcon icon={faGears} /> Génération en cours...
            </div>
          </div>
        )}
        {formData.sections.map((section, index) => (
          <div key={section.name}>
            <span
              className={clsx(
                reportItemLabel,
                generateReportMutation.isPending ? reportItemBlur : null
              )}
            >
              {section.name}
            </span>
            <span
              id={`report_${data?.id ?? 'new'}_section_${index}`}
              contentEditable={
                isContentEditable && !generateReportMutation.isPending
              }
              className={clsx(
                reportItemValue,
                generateReportMutation.isPending ? reportItemBlur : null
              )}
              data-placeholder={'Saisir...'}
              suppressContentEditableWarning={true}
              // biome-ignore lint/security/noDangerouslySetInnerHtml: content is sanitized
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(section.content)
              }}
              onClick={() => setIsContentEditable(true)}
              onBlur={(e) => {
                const newSections = formData.sections.map((s) =>
                  s.name === section.name
                    ? {
                        ...s,
                        content: e.currentTarget.innerHTML || ''
                      }
                    : s
                )
                updateReport({
                  ...formData,
                  sections: newSections
                })
              }}
            />
          </div>
        ))}
      </div>
    </motion.div>
  )
}
